import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endpoints";

export interface User {
  id: string;
  _id?: string;
  email: string;
  name: string;
  role: "talking" | "listener" | "admin";

  avatar?: string;
  bio?: string;
  skills?: string[];
  languages?: string[];
  kycStatus?: "none" | "pending" | "verified" | "rejected";
  listenerType?: string;
  points?: number;
  sessionsCompleted?: number;
  isPremium?: boolean;
  activePlanId?: string;
  processedPayments?: string[];
  freeTrialUsed?: boolean;
  freeTrialMinutes?: number;
  gender?: "male" | "female" | "other";
  earnings?: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  isAvailable: boolean;
  // Forgot Password / Reset Password
  passwordResetSent: boolean;
  passwordResetSuccess: boolean;
  forgotPasswordEmail: string | null;
  // Email Verification
  emailVerified: boolean;
  isUnverified: boolean;
  unverifiedEmail: string | null;
}

const getStoredUser = (): User | null => {
  try {
    const item = localStorage.getItem("user");
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

const initialStoredUser = getStoredUser();
const initialStoredToken = localStorage.getItem("token");

const initialState: AuthState = {
  user: initialStoredUser,
  token: initialStoredToken,
  refreshToken: localStorage.getItem("refreshToken"),
  loading: false,
  error: null,
  initialized: !!(initialStoredToken && initialStoredUser),
  isAvailable: localStorage.getItem("isAvailable") === "true",
  passwordResetSent: false,
  passwordResetSuccess: false,
  forgotPasswordEmail: null,
  emailVerified: false,
  isUnverified: false,
  unverifiedEmail: null,
};

// ──────────────────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────────────────

export const register = createAsyncThunk(
  "auth/register",
  async (
    userData: {
      name: string;
      email: string;
      password: any;
      role: string;
      gender: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.REGISTER, userData);
      return { email: userData.email, message: response.data.message };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const login = createAsyncThunk(
  "auth/login",
  async (
    credentials: { email: string; password: any; role: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, credentials);
      const { token, refreshToken, user } = response.data;
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      return { token, refreshToken, user };
    } catch (error: any) {
      const errData = error.response?.data;
      return rejectWithValue({
        message: errData?.message || "Login failed",
        isUnverified: errData?.isUnverified || false,
        email: errData?.email || credentials.email,
      });
    }
  },
);

export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async (data: { idToken: string; role?: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.GOOGLE, data);
      const { token, refreshToken, user } = response.data;
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      return { token, refreshToken, user };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Google Login failed",
      );
    }
  },
);

export const refreshTokenThunk = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      const response = await apiClient.post(ENDPOINTS.AUTH.REFRESH_TOKEN, {
        refreshToken: storedRefreshToken,
      });
      const { token, refreshToken: newRefreshToken } = response.data;
      if (token) {
        localStorage.setItem("token", token);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
      }
      return { token, refreshToken: newRefreshToken };
    } catch (error: any) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      return rejectWithValue(
        error.response?.data?.message || "Failed to refresh token",
      );
    }
  },
);

export const getMe = createAsyncThunk(
  "auth/getMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(ENDPOINTS.AUTH.ME);
      return response.data; // Expected { user: User }
    } catch (error: any) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user",
      );
    }
  },
);

export const forgotPasswordThunk = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email,
      });
      return {
        email,
        message: response.data?.message || "OTP sent to your email",
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send reset email",
      );
    }
  },
);

export const verifyEmailThunk = createAsyncThunk(
  "auth/verifyEmail",
  async (data: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(ENDPOINTS.AUTH.VERIFY_EMAIL, data);
      const { token, refreshToken, user } = response.data;
      if (token) {
        localStorage.setItem("token", token);
      }
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      return { token, refreshToken, user, message: response.data?.message };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Invalid or expired OTP",
      );
    }
  },
);

export const resetPasswordThunk = createAsyncThunk(
  "auth/resetPassword",
  async (
    data: { email: string; otp: string; newPassword: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post(
        ENDPOINTS.AUTH.RESET_PASSWORD,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password",
      );
    }
  },
);

export const updateAvailabilityThunk = createAsyncThunk(
  "auth/updateAvailability",
  async (isAvailable: boolean, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setAvailability(isAvailable));
      const response = await apiClient.patch("/api/users/availability", { isAvailable });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update availability status",
      );
    }
  },
);

// ──────────────────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAvailable = false;
      state.initialized = false;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("isAvailable");
    },
    setTokens: (
      state,
      action: PayloadAction<{ token: string; refreshToken?: string }>,
    ) => {
      state.token = action.payload.token;
      localStorage.setItem("token", action.payload.token);
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem("refreshToken", action.payload.refreshToken);
      }
    },
    clearError: (state) => {
      state.error = null;
      state.isUnverified = false;
      state.unverifiedEmail = null;
    },
    clearPasswordReset: (state) => {
      state.passwordResetSent = false;
      state.passwordResetSuccess = false;
      state.forgotPasswordEmail = null;
      state.error = null;
    },
    clearEmailVerified: (state) => {
      state.emailVerified = false;
      state.error = null;
    },
    setAvailability: (state, action: PayloadAction<boolean>) => {
      state.isAvailable = action.payload;
      localStorage.setItem("isAvailable", action.payload.toString());
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      } else if (action.payload) {
        state.user = action.payload as User;
      }
      if (state.user) {
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isUnverified = false;
        state.unverifiedEmail = null;
      })
      .addCase(
        login.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            refreshToken?: string;
            user: User;
          }>,
        ) => {
          state.loading = false;
          state.initialized = true;
          state.token = action.payload.token;
          if (action.payload.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
          }
          state.user = action.payload.user;
          state.isUnverified = false;
          state.unverifiedEmail = null;
          if (action.payload.user) {
            localStorage.setItem("user", JSON.stringify(action.payload.user));
          }
        },
      )
      .addCase(login.rejected, (state, action: any) => {
        state.loading = false;
        if (
          typeof action.payload === "object" &&
          action.payload?.isUnverified
        ) {
          state.isUnverified = true;
          state.unverifiedEmail = action.payload.email;
          state.error = action.payload.message;
        } else {
          state.isUnverified = false;
          state.unverifiedEmail = null;
          state.error =
            (typeof action.payload === "string"
              ? action.payload
              : action.payload?.message) || "Login failed";
        }
      })
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        googleLogin.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            refreshToken?: string;
            user: User;
          }>,
        ) => {
          state.loading = false;
          state.initialized = true;
          state.token = action.payload.token;
          if (action.payload.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
          }
          state.user = action.payload.user;
          if (action.payload.user) {
            localStorage.setItem("user", JSON.stringify(action.payload.user));
          }
        },
      )
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Refresh Token
      .addCase(refreshTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        refreshTokenThunk.fulfilled,
        (
          state,
          action: PayloadAction<{ token: string; refreshToken?: string }>,
        ) => {
          state.loading = false;
          state.token = action.payload.token;
          if (action.payload.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
          }
        },
      )
      .addCase(refreshTokenThunk.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.error = action.payload as string;
      })
      // Get Me
      .addCase(getMe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getMe.fulfilled,
        (state, action: PayloadAction<{ user: User & { isAvailable?: boolean } }>) => {
          state.loading = false;
          state.initialized = true;
          if (action.payload?.user) {
            state.user = action.payload.user;
            if (typeof action.payload.user.isAvailable === "boolean") {
              state.isAvailable = action.payload.user.isAvailable;
              localStorage.setItem("isAvailable", action.payload.user.isAvailable.toString());
            }
            localStorage.setItem("user", JSON.stringify(action.payload.user));
          }
        },
      )
      .addCase(getMe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.initialized = true;
        const errStr = String(action.payload || "").toLowerCase();
        if (
          errStr.includes("unauthorized") ||
          errStr.includes("jwt") ||
          errStr.includes("invalid")
        ) {
          state.token = null;
          state.refreshToken = null;
          state.user = null;
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
        }
      })
      // Forgot Password
      .addCase(forgotPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordResetSent = false;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordResetSent = true;
        state.forgotPasswordEmail = action.payload.email;
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify Email
      .addCase(verifyEmailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.emailVerified = false;
      })
      .addCase(
        verifyEmailThunk.fulfilled,
        (
          state,
          action: PayloadAction<{
            token?: string;
            refreshToken?: string;
            user?: User;
          }>,
        ) => {
          state.loading = false;
          state.emailVerified = true;
          if (action.payload?.token) {
            state.token = action.payload.token;
          }
          if (action.payload?.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
          }
          if (action.payload?.user) {
            state.user = action.payload.user;
          }
        },
      )
      .addCase(verifyEmailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reset Password
      .addCase(resetPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordResetSuccess = false;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.passwordResetSuccess = true;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  logout,
  setTokens,
  clearError,
  clearPasswordReset,
  clearEmailVerified,
  setAvailability,
  updateUser,
} = authSlice.actions;

export default authSlice.reducer;
